import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Phone,
  Settings,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  Globe,
  Link as LinkIcon,
  CheckCircle2,
  ExternalLink,
  Send,
  Loader2,
  TestTube } from
'lucide-react';

const API_URL = API_BASE;

// Default config structure used as fallback
const defaultSMSConfig = {
  enabled: true,
  provider: 'Twilio',
  accountSid: '',
  authToken: '',
  phoneNumbers: [],
  messageQuota: { monthly: 10000, used: 0, remaining: 10000 },
  deliveryCallback: ''
};

export const SMSSettings = () => {const { t } = useTranslation("comms");
  const [config, setConfig] = useState(defaultSMSConfig);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [channelStatus, setChannelStatus] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statusRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/communications/status/sms`),
        fetch(`${API_URL}/api/communications/settings/sms`)]
        );

        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setChannelStatus(statusData);
        }

        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setConfig((prev) => ({
            ...prev,
            enabled: settingsData.enabled ?? prev.enabled,
            provider: settingsData.provider ?? prev.provider,
            accountSid: settingsData.accountSid ?? prev.accountSid,
            authToken: settingsData.authToken ?? prev.authToken,
            phoneNumbers: settingsData.numbers?.map((n, idx) => ({
              id: n.id || `pn_${idx}`,
              number: n.number,
              type: n.type || 'local',
              country: n.country || 'US',
              enabled: n.status === 'active'
            })) || prev.phoneNumbers,
            messageQuota: settingsData.pricing ? {
              monthly: 10000,
              used: 0,
              remaining: 10000
            } : prev.messageQuota,
            deliveryCallback: prev.deliveryCallback
          }));
        }
      } catch (err) {
        console.error('Failed to fetch SMS settings:', err);
        toast.error('Failed to load SMS settings');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSendTestSMS = async () => {
    if (!testPhone || !testMessage) {
      toast.error('Please enter both phone number and message');
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/communications/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'sms',
          recipient: testPhone,
          message: testMessage
        })
      });
      const result = await response.json();

      if (result.status === 'simulated') {
        toast.success('Test SMS sent (SIMULATION MODE)', {
          description: `Message ID: ${result.external_id}`
        });
      } else if (result.status === 'sent') {
        toast.success('Test SMS sent successfully!', {
          description: `Message ID: ${result.external_id}`
        });
      } else {
        toast.error('Failed to send SMS', {
          description: result.error || 'Unknown error'
        });
      }
    } catch (err) {
      toast.error('Failed to send test SMS', {
        description: err.message
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="communications-sms">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Phone className="h-8 w-8 text-orange-600" />{t("smsSettings", "SMS Settings")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureTwilioSms", "Configure Twilio SMS for general communications")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{t("channelStatus", "Channel Status")}</span>
          <Switch checked={config.enabled} />
          <Badge className={config.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
            {config.enabled ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {/* Provider Info */}
      <Card className="card-elevated bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://www.twilio.com/assets/icons/twilio-icon.svg" alt="Twilio" className="h-10 w-10" onError={(e) => e.target.style.display = 'none'} />
              <div>
                <p className="font-semibold">{t("twilio", "Twilio")}</p>
                <p className="text-sm text-muted-foreground">{t("smsGatewayProvider", "SMS Gateway Provider")}</p>
              </div>
            </div>
            <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <ExternalLink className="h-4 w-4 mr-2" />{t("openConsole", "Open Console")}

              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* API Configuration */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />{t("apiConfiguration", "API Configuration")}

          </CardTitle>
          <CardDescription>{t("configureYourTwilio", "Configure your Twilio account credentials")}

          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("accountSid", "Account SID")}</label>
              <Input
                value={config.accountSid}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="font-mono" />
              
              <p className="text-xs text-muted-foreground">{t("foundInYour", "Found in your Twilio Console dashboard")}

              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("authToken", "Auth Token")}</label>
              <div className="relative">
                <Input
                  type={showToken ? 'text' : 'password'}
                  value={config.authToken}
                  placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="pr-20 font-mono" />
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-8 top-0 h-full"
                  onClick={() => setShowToken(!showToken)}>
                  
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full">
                  
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("statusCallbackUrl", "Status Callback URL")}</label>
            <div className="flex gap-2">
              <Input
                value={config.deliveryCallback}
                placeholder="https://api.yourdomain.com/webhooks/sms"
                className="font-mono text-sm" />
              
              <Button variant="outline">
                <LinkIcon className="h-4 w-4 mr-2" />{t("test", "Test")}

              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t("webhookForDelivery", "Webhook for delivery status updates")}

            </p>
          </div>
        </CardContent>
      </Card>

      {/* Phone Numbers */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-muted-foreground" />{t("phoneNumbers", "Phone Numbers")}

            </CardTitle>
            <CardDescription>{t("manageYourTwilio", "Manage your Twilio phone numbers for sending SMS")}

            </CardDescription>
          </div>
          <Button className="gap-2 bg-orange-600 hover:bg-orange-700">
            <Plus className="h-4 w-4" />{t("addNumber", "Add Number")}

          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {config.phoneNumbers.map((phone) =>
            <div key={phone.id} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium font-mono">{phone.number}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs capitalize">{phone.type}</Badge>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Globe className="h-3 w-3" />
                        {phone.country}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Switch checked={phone.enabled} />
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("monthlyUsage", "Monthly Usage")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-3">
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <p className="text-3xl font-bold text-orange-600">{(config.messageQuota?.used ?? 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{t("messagesSent", "Messages Sent")}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <p className="text-3xl font-bold">{(config.messageQuota?.remaining ?? 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{t("remaining", "Remaining")}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <p className="text-3xl font-bold">{(config.messageQuota?.monthly ?? 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{t("monthlyLimit", "Monthly Limit")}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 rounded-full"
                style={{ width: `${config.messageQuota.used / config.messageQuota.monthly * 100}%` }} />
              
            </div>
            <p className="text-xs text-muted-foreground mt-1 text-center">
              {(config.messageQuota.used / config.messageQuota.monthly * 100).toFixed(1)}{t("ofMonthlyQuota", "% of monthly quota used")}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Test SMS */}
      <Card className="card-elevated border-dashed border-2 border-orange-300/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="h-5 w-5 text-orange-600" />{t("sendTestSms", "Send Test SMS")}

            {channelStatus?.simulation_mode &&
            <Badge className="ml-2 bg-amber-100 text-amber-700">{t("simulationMode", "SIMULATION MODE")}</Badge>
            }
          </CardTitle>
          <CardDescription>
            {channelStatus?.simulation_mode ?
            'No Twilio credentials configured - messages will be simulated but not actually sent' :
            'Send a test SMS to verify your configuration'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("phoneNumber", "Phone Number")}</label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+1234567890"
                className="font-mono" />
              
              <p className="text-xs text-muted-foreground">{t("includeCountryCode", "Include country code (e.g., +1 for US)")}

              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("testMessage", "Test Message")}</label>
              <Textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Your shipment is on its way!"
                rows={2}
                maxLength={160} />
              
              <p className="text-xs text-muted-foreground">
                {testMessage.length}{t("160Characters", "/160 characters")}
              </p>
            </div>
          </div>
          <Button
            onClick={handleSendTestSMS}
            disabled={isSending}
            className="bg-orange-600 hover:bg-orange-700">
            
            {isSending ?
            <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("sending", "Sending...")}

            </> :

            <>
                <Send className="h-4 w-4 mr-2" />{t("sendTestSms", "Send Test SMS")}

            </>
            }
          </Button>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline">{t("cancel", "Cancel")}</Button>
        <Button className="bg-orange-600 hover:bg-orange-700">{t("saveChanges", "Save Changes")}</Button>
      </div>
    </div>);

};